import { useEffect } from 'react'
import type { CSSProperties } from 'react'

/*
 * Contextual help — transcribed from the App-shell canon §3.3 (shell.help.panel,
 * FLEXIBLE). Overlay (desktop, 380px right panel) / full content-area sheet
 * (mobile). Toggled in place — NEVER a route, and it must not navigate away
 * from the current page (§3.3 must-nots). Content switches with the active
 * section. DOM ids: help.root / help.head / help.qs / help.contact (§1.3).
 */

export type HelpSection = 'ready' | 'orders' | 'passes' | 'profile'

// Verbatim [chrome] copy — all four Q&A sets are contract copy (canon §3.3;
// answers verbatim from the DC source HELP object).
const HELP: Record<HelpSection, { title: string; blurb: string; qs: { q: string; a: string }[] }> = {
  ready: {
    title: 'Getting ready',
    blurb: 'Everything an event needs from you before your ticket can be issued.',
    qs: [
      { q: 'Why is my ticket not issued yet?', a: 'Tickets are issued once your profile and waiver are done and the organiser has allocated waves. If a step says SOON or AUTO, it is not yours to do.' },
      { q: 'What does “You’re up to date” mean?', a: 'Nothing is waiting on you. The remaining steps are the organiser’s — we will notify you when they open.' },
      { q: 'Can someone else finish my profile?', a: 'Only for a child on your account, or a participant under 18 whose account you manage.' },
    ],
  },
  orders: {
    title: 'Orders and assigning',
    blurb: 'An order holds the tickets you bought. Each ticket holds one or more places.',
    qs: [
      { q: 'How do I give a ticket to someone?', a: 'Open the ticket, then “Please assign this ticket”. Choose yourself, send an email invite, or pick a child on your account.' },
      { q: 'They have not accepted the invite', a: 'A place shows “Invite pending” until they accept. Use Manage to resend or give it to someone else — nothing is lost.' },
      { q: 'The participant is under 18', a: 'We ask their date of birth first. You stay responsible for the waiver and payment, and can still give them their own login.' },
      { q: 'Can I get a refund?', a: 'Refunds are set by the organiser. Contact them from the order, or ask us and we will pass it on.' },
    ],
  },
  passes: {
    title: 'Your tickets',
    blurb: 'Your ticket is your entry. Show the QR code at the gate, or add it to your phone wallet.',
    qs: [
      { q: 'Do I need to print anything?', a: 'No. The QR code on this screen is enough, and works offline once loaded.' },
      { q: 'Where is my bib number?', a: 'Your bib number is shown on your ticket once your place is assigned. Staff confirm it at check-in on the day.' },
      { q: 'I have several tickets', a: 'Step through them one at a time — only one QR code shows at once so the scanner reads the right person.' },
      { q: 'My wave time changed', a: 'The organiser can move waves up to race day. Your ticket and wallet pass update automatically.' },
    ],
  },
  profile: {
    title: 'Your account',
    blurb: 'One profile, reused for every Trumin event. Children you manage sit here too.',
    qs: [
      { q: 'Why do you need my date of birth?', a: 'It sets your age category and, for some tickets, whether you are eligible at all.' },
      { q: 'How do I add a child?', a: 'Profile → Add a child. Name is enough to start; the rest is asked when they enter an event.' },
      { q: 'Can I change my email?', a: 'Yes, from Security and password. Your tickets and history follow the account, not the address.' },
      { q: 'Delete my account', a: 'Ask us and we will confirm what happens to past results before anything is removed.' },
    ],
  },
}

const eyebrow: CSSProperties = {
  fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)',
}

export function HelpPanel({ section, mobile, onClose }: {
  section: HelpSection
  mobile?: boolean
  onClose: () => void
}) {
  const c = HELP[section]
  // Organiser-contact CTA sub-line. Canon §3.3 pattern is
  // `{organiser} — waves, refunds and course questions`, but the active
  // event's organiser + real contact channel is a §12.7 TBD-IN-DC seam not yet
  // wired. Per PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS-V1 (LOCKED) + ruling A,
  // NO demo organiser ships — render the name-less honest sub-line until the
  // resolved active-event organiser is injected here. TODO(§12.7).
  const organiser: string | undefined = undefined

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Layout states per canon §3.3: desktop = right-side overlay panel, 380px,
  // absolute over content; mobile = full content-area sheet (grid area), z-40.
  const sheet: CSSProperties = mobile
    ? {
        gridArea: 'content', zIndex: 40, background: 'var(--bg-help)',
        borderTop: '1px solid var(--hairline-10)', display: 'flex', flexDirection: 'column',
        minHeight: 0, overflow: 'hidden',
      }
    : {
        position: 'absolute', top: 0, right: 0, bottom: 0, width: 380, zIndex: 40,
        background: 'var(--bg-help)', borderLeft: '1px solid var(--hairline-10)',
        boxShadow: 'var(--shadow-help)', display: 'flex', flexDirection: 'column', minHeight: 0,
      }

  return (
    <div id="help.root" data-testid="help-panel" role="dialog" aria-label={`Help — ${c.title}`} style={sheet}>
      {/* header */}
      <div id="help.head" style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 18px 13px', borderBottom: '1px solid var(--hairline-08)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...eyebrow, marginBottom: 3 }}>Help</div>
          <div data-testid="help-title" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>{c.title}</div>
          <div data-testid="help-blurb" style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>{c.blurb}</div>
        </div>
        <button
          data-testid="help-close"
          title="Close help"
          onClick={onClose}
          style={{
            flex: 'none', minWidth: 'var(--tap-min)', minHeight: 'var(--tap-min)', borderRadius: 11,
            border: '1px solid var(--hairline-12)', background: 'transparent',
            color: 'var(--text-secondary)', fontFamily: 'inherit', fontSize: 15, cursor: 'pointer',
          }}
        >✕</button>
      </div>

      {/* scrollable Q&A + escalation */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 18px 18px' }}>
        <div id="help.qs">
          {c.qs.map((h, i) => (
            <div key={h.q} data-testid="help-q" data-q-index={i} style={{ borderBottom: '1px solid var(--hairline-06)', padding: '11px 0' }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 5 }}>{h.q}</div>
              <div style={{ fontSize: 11.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{h.a}</div>
            </div>
          ))}
        </div>

        <div id="help.contact">
          <div style={{ ...eyebrow, margin: '20px 0 10px' }}>Still stuck</div>
          <div style={{ display: 'grid', gap: 9 }}>
            {/* CTAs are unwired in the DC (TBD-IN-DC, canon §3.3 must-nots /
                §12.7) — production wires real support + organiser-contact
                channels. Inert until then; they must never navigate away. */}
            <button
              data-testid="help-cta-support"
              aria-disabled="true"
              style={{
                display: 'flex', alignItems: 'center', gap: 11, width: '100%', minHeight: 52,
                border: 0, borderRadius: 13, background: 'var(--accent)', color: 'var(--accent-ink)',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, cursor: 'pointer',
                padding: '0 14px', textAlign: 'left',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ flex: 'none' }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              <span style={{ flex: 1, minWidth: 0 }}>
                Message support
                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, opacity: 0.72, marginTop: 1 }}>Usually answered within a few hours</span>
              </span>
            </button>
            <button
              data-testid="help-cta-organiser"
              aria-disabled="true"
              style={{
                display: 'flex', alignItems: 'center', gap: 11, width: '100%', minHeight: 52,
                border: '1px solid var(--hairline-14)', borderRadius: 13, background: 'transparent',
                color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                cursor: 'pointer', padding: '0 14px', textAlign: 'left',
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }}><path d="M4 5h16v14H4z" /><path d="M4 7l8 6 8-6" /></svg>
              <span style={{ flex: 1, minWidth: 0 }}>
                Email the organiser
                <span style={{ display: 'block', fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', marginTop: 1 }}>{organiser ? `${organiser} — waves, refunds and course questions` : 'Waves, refunds and course questions'}</span>
              </span>
            </button>
          </div>
          <div data-testid="help-footer-note" style={{ fontSize: 10.5, color: 'var(--text-dim)', lineHeight: 1.5, marginTop: 14 }}>
            Trumin handles your account and tickets. The organiser owns the event itself — waves, rules and refunds.
          </div>
        </div>
      </div>
    </div>
  )
}
